
/*
 * Copyright (C) Siemens AG 2026. All rights reserved. Confidential
 */

import {
    LoadPortalAuthUser, AuthPortalUserSuccess, TenantIdChanged, AuthPortalUserFailure, LoadAvailableNeoSimitVersions, SuccessAvailableNeoSimitVersions, FailAvailableNeoSimitVersions, IdentitySuccess, LoadIdentity, IdentityFailure, LoadCurrentTenantProjects, RefreshCurrentTenantProjects, SuccessCurrentTenantProjects,
    LoadCurrentTenantUsers,
    SuccessCurrentTenantUsers,
    LoadCurrentTenantSubTenants,
    SuccessCurrentTenantSubTenants,
    LoadCurrentTenantPricingPlans,
    SuccessCurrentTenantPricingPlans,
    LoadCurrentTenantPriceList,
    SuccessCurrentTenantPriceList,
    FailureCurrentTenantPriceList,
    LoadAnnouncements,
    SuccessAnnouncements,
    FailureAnnouncements,
    SuccessSingleAnnouncementImage,
    FailureSingleAnnouncementImage
} from "../actions/app.actions"
import { createReducer, on} from "@ngrx/store";
import { AnnouncementState, AuthPortalUserState, AvailableNeoSimitVersionsState, CurrentTenantDataState, IdentityState } from "../app.state";
import { AnnouncementImage } from "src/app/model/announcement.model";



const status = 'not-loaded';

const initialAuthUserState: AuthPortalUserState = {authUser: null, error: false, status: status, currentTenantId: null};
export const authUserStateReducer = createReducer<AuthPortalUserState>(
    initialAuthUserState,
    on(LoadPortalAuthUser, (state, {}) =>{ return {...state, isLoading: true}}),
    on(AuthPortalUserSuccess, (state, {payload}) =>{ return {error: false, isLoading: false, authUser: payload.authUser, isAuth: true, status: 'loaded', currentTenantId: payload.currentTenantId}}),
    on(AuthPortalUserFailure, (state, {}) =>{ return {authUser: null, error: true, status: 'loaded', currentTenantId: null}})
);

const initialAvailableNeoSimitVerionsState: AvailableNeoSimitVersionsState = {error: false, status: null, versions: []}
export const availableNeoSimitVersionsReducer = createReducer<AvailableNeoSimitVersionsState>(
    initialAvailableNeoSimitVerionsState,
    on(LoadAvailableNeoSimitVersions, (state, {payload}) =>{ return {...state, error: false, status: 'loading'}}),
    on(SuccessAvailableNeoSimitVersions, (state, {payload}) => { return {versions: payload, status: 'loaded', error: false}}),
    on(FailAvailableNeoSimitVersions, (state, {}) => { return {versions: [], error: true, status: 'loaded'}})
);

const initialIdentityState: IdentityState = {identity: null, status: null, error: false, idToken: ''}
export const identityStateReducer = createReducer<IdentityState>(
    initialIdentityState,
    on(LoadIdentity, (state, {payload}) => {return {identity: null, status: 'not-checked', error: false, idToken: payload.idToken}}),
    on(IdentitySuccess, (state, {payload}) => {return {...state, identity: payload, status: 'checked', error: false}}),
    on(IdentityFailure, (state, {}) => {return {...state, identity: null, status: 'checked', error: true}})
);

const initialCurrentTenantDataState: CurrentTenantDataState = {projects: [], projectsStatus: status, users: [], usersStatus: status, subTenants: [], subTenantsStatus: status, pricingPlans: [], pricingPlansStatus: status, priceList: null, priceListStatus: status}    
export const currentTenantDataStateReducer = createReducer<CurrentTenantDataState>(
    initialCurrentTenantDataState,
    on(TenantIdChanged, (state) => {
      // Reset all tenant-specific data when tenant changes
      return {
        projects: [],
        projectsStatus: status,
        users: [],
        usersStatus: status,
        subTenants: [],
        subTenantsStatus: status,
        pricingPlans: [],
        pricingPlansStatus: status,
        priceList: null,
        priceListStatus: status
      };
    }),
    on(LoadCurrentTenantProjects, (state, payload) => {return {...state, projects: [], projectsStatus: 'loading'}}),
    on(RefreshCurrentTenantProjects, (state) => {return {...state, projectsStatus: 'loading'}}),
    on(SuccessCurrentTenantProjects, (state, {payload}) => {return {...state, projects: payload, projectsStatus: 'loaded'}}),
    on(LoadCurrentTenantUsers, (state, payload) => {return {...state, users: [], usersStatus: 'loading'}}),
    on(SuccessCurrentTenantUsers, (state, {payload}) => {return {...state, users: payload, usersStatus: 'loaded'}}),
    on(LoadCurrentTenantSubTenants, (state, payload) => {return {...state, subTenants: [], subTenantsStatus: 'loading'}}),
    on(SuccessCurrentTenantSubTenants, (state, {payload}) => {return {...state, subTenants: payload, subTenantsStatus: 'loaded'}}),
    on(LoadCurrentTenantPricingPlans, (state, payload) => {return {...state, pricingPlans: [], pricingPlansStatus: 'loading'}}),
    on(SuccessCurrentTenantPricingPlans, (state, {payload}) => {return {...state, pricingPlans: payload, pricingPlansStatus: 'loaded'}}),
    on(LoadCurrentTenantPriceList, (state) => {return {...state, priceList: null, priceListStatus: 'loading'}}),
    on(SuccessCurrentTenantPriceList, (state, {payload}) => {return {...state, priceList: payload, priceListStatus: 'loaded'}}),
    on(FailureCurrentTenantPriceList, (state) => {return {...state, priceList: null, priceListStatus: 'error'}}),
);

const initialAnnouncementState: AnnouncementState = {announcements: [], status: 'not-loaded', error: false, images: {}}
export const announcementStateReducer = createReducer<AnnouncementState>(
    initialAnnouncementState,
    on(LoadAnnouncements, (state, {}) => {return {...state, announcements: [], status: 'loading', images: {}}}),
    on(SuccessAnnouncements, (state, {payload}) => {

        // Create a map of images with their keys
        // and set their values to null and error to false
        // Images will be loaded later by the image loader action
        const images: {[key: string]: AnnouncementImage} = {}
        for(let announcement of payload){
            for (let content of announcement.contentItems){
                if(content.contentType === 'image'){
                    let imageKey = content.value[0]
                    images[imageKey] = {
                        value: null,
                        error: false,
                        loaded: false
                    }
                }
            }
        }
        
        return {...state, announcements: payload, status: 'loaded', images: images}
    }),
    on(FailureAnnouncements, (state, {}) => {return {...state, announcements: [], status: 'loading', error: true, images: {}}}),
    on(SuccessSingleAnnouncementImage, (state, {payload}) => {
        
        let images = {...state.images}
        images[payload.imageKey] = {
            value: payload.value,
            error: false,
            loaded: true
        }
        return {...state, images: images}
    }),
    on(FailureSingleAnnouncementImage, (state, {payload}) => {
    
        let images = {...state.images}
        images[payload.imageKey] = {
            value: null,
            error: true,
            loaded: true
        }
        return {...state, images: images}
    })
)